import { useState, useEffect } from 'react';
import { FaUser, FaEnvelope, FaBuilding, FaMapMarkerAlt, FaPhone, FaPen } from 'react-icons/fa';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseconfig';
import { useAuthGuard } from '../hooks/useAuthGuard';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Map marker position selector component
const LocationMarker = ({ position, setPosition, isEditing }) => {
  const map = useMapEvents({
    click(e) {
      if (isEditing) {
        setPosition(e.latlng);
      }
    },
  });

  useEffect(() => {
    if (position) {
      map.flyTo(position, map.getZoom());
    }
  }, [position, map]);

  return position ? <Marker position={position} /> : null;
};

const ServiceOwnerProfile = () => {
  useAuthGuard('serviceOwner');
  const [profile, setProfile] = useState({
    displayName: '',
    email: '',
    businessName: '',
    businessAddress: '',
    phone: '',
    joinDate: '',
    location: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        
        if (!userData || !userData.uid) {
          throw new Error('User data not found');
        }

        const userDoc = await getDoc(doc(db, 'users', userData.uid));
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setProfile({
            displayName: data.displayName || '',
            email: userData.email || '',
            businessName: data.businessName || '',
            businessAddress: data.businessAddress || '',
            phone: data.phone || '',
            joinDate: data.createdAt ? new Date(data.createdAt).toLocaleDateString() : 'N/A',
            location: data.location || null,
          });
          setFormData({
            displayName: data.displayName || '',
            businessName: data.businessName || '',
            businessAddress: data.businessAddress || '',
            phone: data.phone || '',
          });
          if (data.location) {
            setPosition({ lat: data.location.lat, lng: data.location.lng });
          }
        } else {
          throw new Error('User profile not found');
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      
      if (!userData || !userData.uid) {
        throw new Error('User data not found');
      }
      
      const updateData = {
        displayName: formData.displayName,
        businessName: formData.businessName,
        businessAddress: formData.businessAddress,
        phone: formData.phone,
        updatedAt: new Date().toISOString(),
      };

      if (position) {
        updateData.location = {
          lat: position.lat,
          lng: position.lng,
        };
      }
      
      await updateDoc(doc(db, 'users', userData.uid), updateData);
      
      setProfile({
        ...profile,
        ...formData,
        location: position,
      });
      
      if (formData.displayName !== userData.displayName) {
        localStorage.setItem(
          'user', 
          JSON.stringify({
            ...userData,
            displayName: formData.displayName
          })
        );
      }
      
      setIsEditing(false);
      setUpdateSuccess(true);
      
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="w-full max-w-md p-8">
          <div className="text-center">Loading profile...</div>
        </div>
      </div>
    );
  }

  const defaultCenter = { lat: 7.8731, lng: 80.7718 }; // Default center (Sri Lanka)

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="bg-blue-600 px-6 py-4">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-bold text-white">Service Owner Profile</h1>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center bg-white text-blue-600 px-3 py-1 rounded-md hover:bg-blue-50"
                >
                  <FaPen className="mr-2" /> Edit Profile
                </button>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {updateSuccess && (
            <div className="bg-green-50 border-l-4 border-green-500 p-4">
              <p className="text-green-700">Profile updated successfully!</p>
            </div>
          )}

          {isEditing ? (
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="displayName"
                      name="displayName"
                      type="text"
                      required
                      value={formData.displayName}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    <FaUser className="absolute right-3 top-2.5 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      disabled
                      value={profile.email}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 bg-gray-50 rounded-md shadow-sm sm:text-sm"
                    />
                    <FaEnvelope className="absolute right-3 top-2.5 text-gray-400" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
                </div>

                <div>
                  <label htmlFor="businessName" className="block text-sm font-medium text-gray-700">
                    Business Name
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="businessName"
                      name="businessName"
                      type="text"
                      required
                      value={formData.businessName}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    <FaBuilding className="absolute right-3 top-2.5 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label htmlFor="businessAddress" className="block text-sm font-medium text-gray-700">
                    Business Address
                  </label>
                  <div className="mt-1 relative">
                    <textarea
                      id="businessAddress"
                      name="businessAddress"
                      rows="3"
                      required
                      value={formData.businessAddress}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                    <FaPhone className="absolute right-3 top-2.5 text-gray-400" />
                  </div>
                </div>

                {/* Map Section */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Service Center Location
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Click on the map to set your service center location
                  </p>
                  <div className="h-[400px] w-full rounded-lg overflow-hidden border border-gray-300">
                    <MapContainer
                      center={position || defaultCenter}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <LocationMarker position={position} setPosition={setPosition} isEditing={true} />
                    </MapContainer>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="bg-gray-200 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div className="p-6">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <FaUser className="mr-2 text-blue-500" /> Full Name
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{profile.displayName}</dd>
                </div>

                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <FaEnvelope className="mr-2 text-blue-500" /> Email
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{profile.email}</dd>
                </div>

                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <FaBuilding className="mr-2 text-blue-500" /> Business Name
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{profile.businessName}</dd>
                </div>

                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <FaPhone className="mr-2 text-blue-500" /> Phone Number
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{profile.phone || 'Not provided'}</dd>
                </div>

                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-blue-500" /> Business Address
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">{profile.businessAddress}</dd>
                </div>

                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Member Since</dt>
                  <dd className="mt-1 text-sm text-gray-900">{profile.joinDate}</dd>
                </div>

                {/* Map View */}
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500 flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-blue-500" /> Service Center Location
                  </dt>
                  <dd className="mt-1">
                    <div className="h-[400px] w-full rounded-lg overflow-hidden border border-gray-300">
                      <MapContainer
                        center={position || defaultCenter}
                        zoom={13}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        {position && <Marker position={position} />}
                      </MapContainer>
                    </div>
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceOwnerProfile;